import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumCell } from "./NumCell";
import { OVERHEAD_FREQS, rowCells, money } from "./rateMath";
import { FREQ_LABEL } from "./SimpleInputs";

const OverheadRowView = ({ row, idx, onChange, onDelete }) => {
  const cells = rowCells(row);
  return (
    <tr className="border-t border-gray-100" data-testid={`overhead-row-${idx}`}>
      <td className="py-2 pr-2">
        <Input
          value={row.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Expense name"
          className="h-9 text-sm border-gray-200 min-w-[170px]"
          data-testid={`overhead-name-${idx}`}
        />
      </td>
      {OVERHEAD_FREQS.map((f) => (
        <td key={f} className="py-2 px-1">
          <NumCell
            value={cells[f]}
            onChange={(v) => onChange({ amount: v, frequency: f })}
            prefix="$"
            min={0}
            className={`min-w-[100px] ${row.frequency === f && row.amount ? "border-pink-300 bg-pink-50/40" : ""}`}
            testId={`overhead-${f}-${idx}`}
          />
        </td>
      ))}
      <td className="py-2 pl-1">
        <Button variant="ghost" size="icon" onClick={onDelete} className="h-8 w-8 text-gray-400 hover:text-red-600" data-testid={`overhead-delete-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
};

export const OverheadTable = ({ rows, onRowsChange, yearlyTotal }) => {
  const update = (i, patch) => onRowsChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => onRowsChange([...rows, { id: `oh-${Date.now()}`, name: "", amount: 0, frequency: "monthly" }]);
  return (
    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50" data-testid="overhead-table">
      <div className="text-sm font-medium text-gray-800">Overhead</div>
      <p className="text-xs text-gray-500 mt-1 mb-3 leading-relaxed">
        Your running costs that aren't billed to a specific job. Type each bill into whichever box matches how it arrives — the other three update instantly.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-1 font-medium">Expense</th>
              {OVERHEAD_FREQS.map((f) => <th key={f} className="pb-1 px-1 font-medium">{FREQ_LABEL[f]}</th>)}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <OverheadRowView key={row.id} row={row} idx={i} onChange={(p) => update(i, p)} onDelete={() => onRowsChange(rows.filter((_, j) => j !== i))} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" size="sm" onClick={add} className="h-8 text-xs" data-testid="overhead-add-btn">
          <Plus className="w-3 h-3 mr-1.5" /> Add Expense
        </Button>
        <div className="text-xs text-gray-600" data-testid="overhead-total">
          Total: <span className="font-mono font-semibold text-gray-900">{money(yearlyTotal / 12)}</span>/month ·{" "}
          <span className="font-mono font-semibold text-gray-900">{money(yearlyTotal)}</span>/year
        </div>
      </div>
    </div>
  );
};
